import { useState } from 'react';
import { Loader2, Play, Link2, CheckCircle2, XCircle, AlertTriangle, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function StepBadge({ ok }) {
  if (ok) {
    return (
      <Badge className="bg-green-600 hover:bg-green-700 text-white gap-1 text-[10px]">
        <CheckCircle2 className="h-3 w-3" />
        OK
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1 text-[10px]">
      <XCircle className="h-3 w-3" />
      FAIL
    </Badge>
  );
}

const STEP_LABELS = {
  env_check: 'Env Vars',
  create_user: 'Create User',
  authenticate_user: 'Authenticate',
  create_connect_session: 'Connect Session',
};

export function BridgeConnectChainDryrunPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.bridgeConnectChainDryrun();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const allOk = result?.steps?.length > 0 && result.steps.every(s => s.ok);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Link2 className="h-5 w-5 text-indigo-600" />
            Bridge Connect Chain
          </CardTitle>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Run Connect Chain
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Exercises the full create_user, authenticate, create_connect_session chain against Bridge, then cleans up. Creates and deletes a real Bridge user; no bank is linked.
        </p>
      </CardHeader>
      {result && (
        <CardContent className="space-y-4">
          {/* Overall banner */}
          <div className={`border rounded-lg p-3 ${allOk ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'}`}>
            <div className="flex items-center gap-2 text-sm font-medium">
              {allOk ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-green-700 dark:text-green-400">
                    Connect chain succeeded server-side (520 is in the redirect leg)
                  </span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <span className="text-red-700 dark:text-red-400">
                    Connect chain failed (see failing step below)
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Step results */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
              Steps
            </h4>
            <div className="space-y-2">
              {result.steps?.map((step, i) => (
                <div key={i} className="border rounded-lg p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <StepBadge ok={step.ok} />
                    <span className="text-sm font-medium">{STEP_LABELS[step.step] || step.step}</span>
                    {step.upstream_status && (
                      <span className="text-xs text-muted-foreground font-mono">
                        HTTP {step.upstream_status}
                      </span>
                    )}
                  </div>
                  {step.upstream_body && (
                    <pre className="mt-1 p-2 bg-muted rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
                      {typeof step.upstream_body === 'object'
                        ? JSON.stringify(step.upstream_body, null, 2)
                        : String(step.upstream_body)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Cleanup */}
          {result.cleanup && (
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                Cleanup
              </h4>
              <div className="border rounded-lg p-3 flex items-center gap-2 text-xs">
                <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                {result.cleanup.ok ? (
                  <span className="text-green-600 dark:text-green-400">
                    Diagnostic user deleted (HTTP {result.cleanup.status})
                  </span>
                ) : (
                  <span className="text-amber-600 dark:text-amber-400">
                    Cleanup failed: {result.cleanup.detail || `HTTP ${result.cleanup.status}`}
                  </span>
                )}
              </div>
            </div>
          )}

          {result.checked_at && (
            <p className="text-[10px] text-muted-foreground text-right">
              Checked at {new Date(result.checked_at).toLocaleString()}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
